import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { Category, FinnairItineraryItemFlight, FinnairPassengerItem, FinnairServiceItem } from '@fcom/dapi/api/models';
import { cartPassengersSelector } from '@fcom/booking/store/selectors';
import { combinedCartServicesMap } from '@fcom/booking/store/selectors/booking-cart-state.selector';
import { ProductCardVariant } from '@fcom/ui-components';
import {
  NonSeatBundleAvailability,
  nonSeatBundleConstituentsByFlightSelector,
  nonSeatBundlesByFlightAndPassengerSelector,
} from '@fcom/common-booking/store';
import { asTravelerService } from '@fcom/common-booking/store/utils/services.utils';
import { ServiceSelections } from '@fcom/dapi/interfaces';
import { serviceCategoriesMapSelector, serviceSelections } from '@fcom/common-booking/store/selectors';
import { ServicesActions } from '@fcom/common-booking/store/actions';

import { getServicesNotifications, isBundleIncludedInPassengerTierBenefit } from '../../utils';
import { EligibilitiesForFragments, Notification } from '../../interfaces';
import { SmpProduct } from '../product-list/product-list.component';

@Component({
  selector: 'fin-ancillary-bundle-selection',
  templateUrl: './ancillary-bundle-selection.component.html',
  styleUrls: ['./ancillary-bundle-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AncillaryBundleSelectionComponent {
  readonly ProductCardVariant = ProductCardVariant;

  readonly category = input.required<Category>();
  readonly flight = input.required<FinnairItineraryItemFlight>();
  readonly product = input<SmpProduct | undefined>();
  readonly hideZeroPrices = input(false);
  readonly availability = input<EligibilitiesForFragments>({});

  private store$ = inject(Store);

  private selections = this.store$.selectSignal(serviceSelections);

  private cartPassengers = this.store$.selectSignal(cartPassengersSelector);
  readonly passengers = computed(() => this.cartPassengers() || []);

  readonly selectionsByPax = computed(() => {
    const selectionsForCategory = this.selections()?.[this.category() as keyof ServiceSelections];
    return selectionsForCategory?.[this.flight()?.id] ?? {};
  });

  private bundleAvailabilityByCategory = this.store$.selectSignal(nonSeatBundlesByFlightAndPassengerSelector);
  readonly availabilityByPax = computed(
    () => this.bundleAvailabilityByCategory()[this.category()]?.[this.flight()?.id] ?? {}
  );

  private constituentsByCategory = this.store$.selectSignal(nonSeatBundleConstituentsByFlightSelector);
  readonly constituents = computed(() => this.constituentsByCategory()[this.category()]?.[this.flight()?.id] ?? []);

  private combinedServices = toSignal(this.store$.pipe(combinedCartServicesMap()), {
    initialValue: {} as Record<string, FinnairServiceItem>,
  });

  readonly tierBenefitByPax = computed<Record<string, boolean>>(() => {
    const combinedServices = this.combinedServices() ?? {};
    const flightId = this.flight()?.id;
    return this.passengers().reduce(
      (acc, passenger) => {
        acc[passenger.id] = isBundleIncludedInPassengerTierBenefit(
          this.category(),
          passenger.id,
          flightId,
          combinedServices
        );
        return acc;
      },
      {} as Record<string, boolean>
    );
  });

  readonly firstAvailability = computed((): NonSeatBundleAvailability | null => {
    return Object.values(this.availabilityByPax()).find((a) => a?.availableInCatalog) ?? null;
  });

  private catalogServicesByCategory = this.store$.selectSignal(serviceCategoriesMapSelector);

  readonly notifications = computed<Notification[]>(() => {
    const flight = this.flight();
    if (!flight) {
      return [];
    }
    const services = this.catalogServicesByCategory()?.[this.category()]?.[flight.id] ?? {};
    return getServicesNotifications(services, this.availability() ?? {}, this.category(), flight);
  });

  readonly selectionBlocked = computed<boolean>(() =>
    this.notifications().some(({ blockSelection }) => blockSelection)
  );

  addCombo(passenger: FinnairPassengerItem): void {
    this.store$.dispatch(
      ServicesActions.setSelectionForFragmentForTraveler({
        category: this.category(),
        fragmentId: this.flight().id,
        travelerId: passenger.id,
        selection: asTravelerService(this.availabilityByPax()[passenger.id].bundleItem),
      })
    );
  }

  removeCombo(passenger: FinnairPassengerItem): void {
    this.store$.dispatch(
      ServicesActions.setSelectionForFragmentForTraveler({
        category: this.category(),
        fragmentId: this.flight().id,
        travelerId: passenger.id,
        selection: undefined,
      })
    );
  }
}
